import { Corban, CorbanApiCredential } from "@/models/corban.model";
import { axiosApiGranatechInstance, axiosInstance } from "./axiosInstance";

export const createCorban = async (
  corbanData: Partial<Corban>
): Promise<Corban> => {
  const response = await axiosInstance.post("/api/admin/corban", corbanData);
  return response.data;
};

export const updateCorbanStatus = async (
  id: number | string,
  status: "PENDING" | "APPROVED" | "REJECTED"
): Promise<Response> => {
  const response = await axiosInstance.put(`/api/admin/corban/${id}`, {
    status,
  });
  return response.data;
};

export const updateCorban = async (
  id: number | string,
  corbanData: any
): Promise<Response> => {
  const response = await axiosInstance.put(
    `/api/admin/corban/${id}`,
    corbanData
  );
  return response.data;
};

export const setCorbanSellerAuthorized = async (corbanId: number, sellerDocument: string): Promise<void> => {
  try {
    await axiosInstance.put(`/api/corban/${corbanId}/seller-authorized`, { 
      sellerAuthorizedDocument: sellerDocument 
    });
  } catch (error) {
    console.error("Erro ao definir vendedor autorizado:", error);
    throw error;
  }
};

export const deactivateCorban = async (id: number): Promise<void> => {
  try {
    await axiosInstance.patch(`/api/user/corban/${id}/disable`);
  } catch (error) {
    throw new Error("Erro ao desativar o usuário Corban");
  }
};

export const getCorbans = async (): Promise<Corban[]> => {
  try {
    const response = await axiosInstance.get<Corban[]>("/api/corban");
    return response.data;
  } catch (error) {
    console.error("Erro ao obter getCorbans:", error);
    throw error;
  }
};

export const checkPendingCorbans = async (
  page: number,
  pageSize: number,
  sortBy?: string,
  sortDirection?: string,
  search?: string,
  status?: string
): Promise<any> => {
  try {
    const response = await axiosInstance.get(
      "/api/payment-split-solicitation/pending-corbans",
      {
        params: {
          page,
          pageSize,
          sortBy,
          sortDirection,
          search,
          status: status || undefined,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao obter corbans pendentes", error);
  }
};

export const getCorbansPaginated = async (
  page: number,
  pageSize: number,
  sortBy?: string,
  sortDirection?: string,
  pending?: boolean,
  search?: string
): Promise<{ data: Corban[]; totalPages: number }> => {
  try {
    const response = await axiosInstance.get<{
      status: "success" | "error";
      data: Corban[];
      totalPages: number;
      page: number;
      pageSize: number;
      sortBy: string;
      sortDirection: string;
      search: string;
    }>(`/api/corbans`, {
      params: {
        page,
        pageSize,
        sortBy,
        sortDirection,
        status: pending ? "PENDING" : null,
        search,
      },
    });

    const corbansData = response.data.data;

    return {
      data: corbansData,
      totalPages: response.data.totalPages,
    };
  } catch (error) {
    console.error("Erro ao obter getCorbanspAGINATED:", error);
    throw error;
  }
};

export const getCorbansByCreditPromoterIdPaginated = async (
  page: number,
  pageSize: number,
  id?: number | null,
  sortBy?: string,
  sortDirection?: string,
  search?: string
): Promise<{ data: Corban[]; totalPages: number }> => {
  if (!id) {
    throw new Error("ID is required");
  }
  try {
    const response = await axiosInstance.get<{
      status: "success" | "error";
      data: Corban[];
      totalPages: number;
      page: number;
      pageSize: number;
      sortBy: string;
      sortDirection: string;
      search: string;
    }>("/api/corbans/credit-promoter", {
      params: {
        page,
        pageSize,
        creditPromoterId: id,
        sortBy,
        sortDirection,
        search,
      },
    });

    const corbansData = response.data.data;

    return {
      data: corbansData,
      totalPages: response.data.totalPages,
    };
  } catch (error) {
    console.error("Erro ao obter Corbans By Credit:", error);
    throw error;
  }
};

export const getCorbanById = async (
  corbanId: any
): Promise<Corban | null> => {
  if (!corbanId) return null;

  try {
    const response = await axiosInstance.get(`/api/corban/${corbanId}`);

    return response.data;
  } catch (error) {
    console.error("Erro ao obter o corban:", error);

    throw error;
  }
};

export const deleteCorban = async (
  corbanId: string | number | undefined
): Promise<Corban | null> => {
  if (!corbanId) return null;

  try {
    const response = await axiosInstance.delete(`/api/corban/${corbanId}`);

    return response.data;
  } catch (error) {
    console.error("Erro ao deletar o corban:", error);

    throw error;
  }
};

export const corbanSelect = async (baseUrl: string): Promise<Corban[]> => {
  const response = await axiosInstance.get<{
    data: Corban[];
    totalPages: number;
  }>(baseUrl);
  return response.data.data;
};

export const getCorbansByName = async (
  searchTerm: string,
  page: number = 1,
  pageSize: number = 10,
  promoterId?: number | undefined
): Promise<{ data: Corban[]; totalPages: number }> => {
  try {
    const response = await axiosInstance.get<{
      data: Corban[];
      totalPages: number;
    }>("/api/corbans-names", {
      params: {
        businessName: searchTerm,
        page,
        pageSize,
        promoterId,
      },
    });

    return {
      data: response.data.data,
      totalPages: response.data.totalPages,
    };
  } catch (error) {
    console.error("Erro ao buscar nomes de Corban:", error);
    throw error;
  }
};

// Create Split Solicitation
export const createSplitSolicitation = async (
  corbanId: number
): Promise<void> => {
  try {
    const response = await axiosInstance.post(
      `/api/payment-split-solicitation`,
      { corbanId }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao criar solicitação de split:", error);
    throw error;
  }
};

// Update Split Solicitation Status
export const updateSplitSolicitationStatus = async (data: {
  corbanId: number;
  status: string;
  rejectionReason?: string;
  rejectedBy?: "FINANCIAL" | "CADASTRO";
  solicitationId: string;
}): Promise<void> => {
  try {
    const response = await axiosInstance.put(
      `/api/payment-split-solicitation/${data.solicitationId}`,
      data
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao atualizar status do split:", error);
    throw error;
  }
};
export const updateCorbanProduct = async (
  id: number,
  corbanProduct: { consig_products: any[], fgts_products: any[] }
): Promise<Response> => {
  const response = await axiosInstance.put(`/api/corban-products/${id}`, corbanProduct);
  return response.data;
};

export const createUserApiFGTS = async (body: { granatech_password_id: string, webhook_fgts: string, corban_id: number, granatech_user_id: string, token?: string }) => {
  try {
    const response = await axiosApiGranatechInstance.post(`/api/v1/users/admin/fgts/client`, body);
    return response.data
  } catch (error) {
    console.error('Error at creating api user for fgts', error)
  }
}

export const createUserApiConsig = async (body: { granatech_password_id: string, webhook_consignado: string, corban_id: number, granatech_user_id: string, token?: string }) => {
  try {
    const response = await axiosApiGranatechInstance.post(`/api/v1/users/admin/consignado/client`, body);
    return response.data
  } catch (error) {
    console.error('Error at create api user for consig', error)
  }
}

export const resetApiKeyFGTS = async (body: { granatech_user_id: string }) => {
  const response = await axiosApiGranatechInstance.patch(`/api/v1/users/admin/fgts/reset-api-key`, body);
  return response.data
}

export const resetApiKeyConsig = async (body: { granatech_user_id: string }) => {
  const response = await axiosApiGranatechInstance.patch(`/api/v1/users/admin/consignado/reset-api-key`, body);
  return response.data
}

export const getCorbanApisDetails = async (corbanId: number): Promise<CorbanApiCredential[]> => {
  const response = await axiosApiGranatechInstance.get(`/api/v1/users/admin/${corbanId}`);
  return response.data
}

export const resetFGTSWebhook = async (body: { granatech_user_id: string, webhook_fgts: string, token?: string }) => {
  const response = await axiosApiGranatechInstance.patch('/api/v1/users/admin/fgts/reset-webhook', body)
  return response.data
}

export const reseConsigntWebhook = async (body: { granatech_user_id: string, webhook_consignado: string, token?: string }) => {
  const response = await axiosApiGranatechInstance.patch('/api/v1/users/admin/consignado/reset-webhook', body)
  return response.data
}





